import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AMAZON_URL,
  BOOK_TITLE,
  BOOK_AUTHOR,
  BOOK_COVER,
  BOOK_TAGLINE,
  IG_HANDLE,
  IG_URL,
} from '../lib/bookLinks';
import './BookPage.css';

const CHAPTERS: Array<{ num: string; title: string; blurb: string }> = [
  { num: 'Intro', title: 'Why Boring Wins', blurb: 'The opposite of exciting isn\'t losing -- it\'s compounding.' },
  { num: '1', title: 'Know Where You Stand', blurb: 'The one number worth tracking, and the 20-minute monthly habit that keeps it honest.' },
  { num: '2', title: 'The Five Pillars & Capital Allocation', blurb: 'Earn, save, invest, pay off debt, plan for later -- and how each one feeds the others.' },
  { num: '3', title: 'Earning: The Source', blurb: 'The loyalty penalty, asking for a raise, and the do-not-do list that protects your time.' },
  { num: '4', title: 'Saving: The Foundation', blurb: 'Defense before offense. Survival number, not lifestyle number. Pay yourself first, automated.' },
  { num: '5', title: 'Investing: Building Wealth', blurb: 'Rule of 72, why index funds usually win, and a simple two-fund setup worth looking into.' },
  { num: '6', title: 'Managing Debt: Break Free', blurb: 'The 7% rule, avalanche vs snowball, and why paying off a card is a guaranteed return.' },
  { num: '7', title: 'Retirement: Your Freedom Number', blurb: 'The number where work is optional, the flaw in the 4% rule, and the Coast FIRE milestone.' },
  { num: '8', title: 'Your Periodic Review System', blurb: 'A rhythm that beats good intentions. The one question to ask every year.' },
  { num: 'End', title: 'Knowing, Doing, Sticking With It', blurb: 'The whole secret: small things, repeated for a long time.' },
];

export const BookPage: React.FC = () => {
  // Public page -- no auth required. Show a sign-in link for visitors who
  // arrived from a marketing surface (IG, Stories) and aren't logged in;
  // show "Dashboard" for visitors who are.
  const { user } = useAuth();
  return (
    <div className="bookpage-wrapper">
      <nav className="book-topnav">
        <Link to="/" className="book-topnav-brand">
          <span className="book-topnav-logo">W</span>
          <span className="book-topnav-name">Wealth Financial Systems</span>
        </Link>
        <div className="book-topnav-links">
          {user ? (
            <Link to="/dashboard" className="book-topnav-link">Dashboard</Link>
          ) : (
            <Link to="/login" className="book-topnav-link">Sign In</Link>
          )}
        </div>
      </nav>
      <div className="bookpage-content">
        {/* ---- Instagram banner (above the fold -- social proof + ongoing
             content invite for visitors who arrive cold from a marketing
             surface). ---- */}
        <a
          href={IG_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="book-ig-banner"
        >
          <span className="book-ig-banner-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
          </span>
          <span className="book-ig-banner-text">
            <span className="book-ig-banner-title">Follow {IG_HANDLE} on Instagram</span>
            <span className="book-ig-banner-sub">Daily market read + clips from the book.</span>
          </span>
          <span className="book-ig-banner-arrow" aria-hidden="true">→</span>
        </a>

        {/* ---- Hero ---- */}
        <section className="book-hero">
          <img
            src={BOOK_COVER}
            alt={`${BOOK_TITLE} -- book cover`}
            className="book-hero-cover"
          />
          <div className="book-hero-text">
            <h1>{BOOK_TITLE}</h1>
            <p className="book-hero-byline">by {BOOK_AUTHOR}</p>
            <p className="book-hero-tagline">{BOOK_TAGLINE}</p>
            <a
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="book-hero-cta"
            >
              Buy on Amazon
            </a>
          </div>
        </section>

        {/* ---- Why this book ---- */}
        <section className="book-section">
          <h2>Why this book exists</h2>
          <p>
            Most money advice arrives in pieces -- save more, invest better,
            pay off debt -- with nothing tying them together. The pieces are
            all true and none of them stick, because there's no system
            underneath.
          </p>
          <p>
            <strong>Get Rich the Boring Way</strong> is that system. It treats
            your money as one connected machine where earning, saving,
            investing, debt payoff, and retirement planning all feed each
            other. You don't need a big income. You don't need to time the
            market. You need a rhythm that runs without you thinking about
            it. The book walks you through how to build that rhythm one boring
            habit at a time.
          </p>
          <p>
            The free tools on this site -- net-worth tracker, FIRE calculator,
            DCA planner, the playbook -- are the operating layer. The book is
            the manual.
          </p>
        </section>

        {/* ---- Chapter list ---- */}
        <section className="book-section">
          <h2>What's inside</h2>
          <div className="book-chapters">
            {CHAPTERS.map((c) => (
              <div key={c.num} className="book-chapter-row">
                <div className="book-chapter-num">{c.num}</div>
                <div className="book-chapter-body">
                  <div className="book-chapter-title">{c.title}</div>
                  <div className="book-chapter-blurb">{c.blurb}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ---- About the author ---- */}
        <section className="book-section">
          <h2>About the author</h2>
          <p>
            Bobby Malik is the founder of Wealth Financial Systems. He spent
            years building software for trading and wealth management, then
            wrote down the boring habit-driven approach that worked for him.
            The book is the same system he uses on his own money -- not the
            flashy version, the one that actually compounded.
          </p>
        </section>

        {/* ---- Final CTA ---- */}
        <section className="book-final-cta">
          <h2>Get the book</h2>
          <p>Available now on Amazon. Paperback and Kindle.</p>
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="book-hero-cta"
          >
            Buy on Amazon
          </a>
        </section>
      </div>
    </div>
  );
};
